/**
 * Settings.
 *
 * Every write goes through the `settings` store (which clamps, persists and notifies) and
 * is followed by `host.onSettingsChanged()`, because the store knows the value changed but
 * only the host can push it into the camera and the input mapper.
 */

import type { VNode } from 'preact';
import { SETTINGS_LIMITS, type QualityChoice, settings } from '../settings.js';
import type { HubHost } from './index.js';
import { Section, useSettings } from './common.js';
import { sound } from '../../audio/sound.js';
import { KEYBINDS } from '../keybinds.js';
import './keybinds.css';

/** Settings → Rendering → Passes. Same names as the URL kill switches in render/renderer.ts. */
const RENDER_SWITCHES: readonly {
  key: 'taa' | 'csm' | 'gtao' | 'noise' | 'bloom';
  label: string;
  hint: string;
}[] = [
  {
    key: 'taa',
    label: 'Temporal anti-aliasing',
    hint: 'TAAU: smooths edges, drives the render scale',
  },
  { key: 'csm', label: 'Cascaded shadows', hint: 'Sun shadows from the Stillsun' },
  { key: 'gtao', label: 'Ambient occlusion', hint: 'Contact shade in corners and under things' },
  { key: 'noise', label: 'Film grain', hint: 'The fine dither over the frame' },
  { key: 'bloom', label: 'Bloom', hint: 'Glow around the sun, lanterns and beams' },
];

/** The reference card that used to sit on the start screen. Read-only for now. */
function KeyBindings(): VNode {
  return (
    <div class="kb-groups">
      {KEYBINDS.map((g) => (
        <div key={g.title}>
          <h3 class="kb-group-title">{g.title}</h3>
          <div class="kb-rows">
            {g.binds.map((b, i) => (
              <div class="kb-row" key={`${g.title}-${String(i)}`}>
                <span class="kb-keys">
                  {b.keys.map((k) => (
                    <kbd class="kb-key" key={k}>
                      {k}
                    </kbd>
                  ))}
                </span>
                <span class="kb-does">{b.does}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export interface SettingsTabProps {
  host: HubHost;
}

interface SliderProps {
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  step: number;
  digits: number;
  onChange: (v: number) => void;
}

function Slider(props: SliderProps): VNode {
  return (
    <label class="hub-field">
      <span class="hub-label">
        {props.label}
        <b class="hub-code">{props.value.toFixed(props.digits)}</b>
      </span>
      <input
        class="hub-range"
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        onInput={(e) => {
          const v = Number(e.currentTarget.value);
          if (Number.isFinite(v)) props.onChange(v);
        }}
      />
      <span class="hub-hint-line">{props.hint}</span>
    </label>
  );
}

const QUALITIES: { id: QualityChoice; label: string }[] = [
  { id: 'auto', label: 'Auto' },
  { id: 'low', label: 'Low' },
  { id: 'high', label: 'High' },
  { id: 'ultra', label: 'Ultra' },
];

export function SettingsTab(props: SettingsTabProps): VNode {
  const s = useSettings();
  const { host } = props;

  const change = <K extends keyof typeof s>(key: K, value: (typeof s)[K]): void => {
    settings.set(key, value);
    host.onSettingsChanged();
  };

  return (
    <div class="hub-pane">
      <Section title="Key bindings" hint="Rebinding comes later">
        <KeyBindings />
      </Section>
      <Section title="Camera" hint="Applies immediately">
        <Slider
          label="Sensitivity"
          hint="Multiplier on every mouse look delta while the right button is held."
          value={s.sensitivity}
          min={SETTINGS_LIMITS.sensitivity[0]}
          max={SETTINGS_LIMITS.sensitivity[1]}
          step={0.05}
          digits={2}
          onChange={(v) => {
            change('sensitivity', v);
          }}
        />
        <Slider
          label="Field of view"
          hint="Vertical field of view, in degrees."
          value={s.fov}
          min={SETTINGS_LIMITS.fov[0]}
          max={SETTINGS_LIMITS.fov[1]}
          step={1}
          digits={0}
          onChange={(v) => {
            change('fov', v);
          }}
        />
        <label class="hub-check">
          <input
            type="checkbox"
            checked={s.invertPitch}
            onChange={(e) => {
              change('invertPitch', e.currentTarget.checked);
            }}
          />
          <span>
            Invert the vertical
            <span class="hub-hint-line">
              Hold the right mouse button to orbit the camera. With this on, pushing the mouse
              forward swings the camera up instead of down.
            </span>
          </span>
        </label>
      </Section>

      <Section title="Audio" hint="Applies immediately">
        <Slider
          label="Master"
          hint="Overall volume, above everything else below."
          value={s.masterVolume}
          min={SETTINGS_LIMITS.masterVolume[0]}
          max={SETTINGS_LIMITS.masterVolume[1]}
          step={0.05}
          digits={2}
          onChange={(v) => {
            change('masterVolume', v);
          }}
        />
        <Slider
          label="Effects"
          hint="Weapons, footsteps and Hymn voices/charges."
          value={s.sfxVolume}
          min={SETTINGS_LIMITS.sfxVolume[0]}
          max={SETTINGS_LIMITS.sfxVolume[1]}
          step={0.05}
          digits={2}
          onChange={(v) => {
            change('sfxVolume', v);
          }}
        />
        <Slider
          label="UI"
          hint="Hit markers, loot pickups and interface feedback."
          value={s.uiVolume}
          min={SETTINGS_LIMITS.uiVolume[0]}
          max={SETTINGS_LIMITS.uiVolume[1]}
          step={0.05}
          digits={2}
          onChange={(v) => {
            change('uiVolume', v);
          }}
        />
        <Slider
          label="Ambience"
          hint="Sexton drones, extract beacons and the Dimming wind."
          value={s.ambienceVolume}
          min={SETTINGS_LIMITS.ambienceVolume[0]}
          max={SETTINGS_LIMITS.ambienceVolume[1]}
          step={0.05}
          digits={2}
          onChange={(v) => {
            change('ambienceVolume', v);
          }}
        />
        <label class="hub-check">
          <input
            type="checkbox"
            checked={s.muted}
            onChange={(e) => {
              change('muted', e.currentTarget.checked);
            }}
          />
          <span>Mute all audio</span>
        </label>
      </Section>

      <Section title="Rendering" hint="Applies on the next reload">
        <div class="hub-field">
          <span class="hub-label">Quality tier</span>
          <div class="hub-chips">
            {QUALITIES.map((q) => (
              <button
                key={q.id}
                type="button"
                class={`hub-chip${s.quality === q.id ? ' on' : ''}`}
                aria-pressed={s.quality === q.id ? 'true' : 'false'}
                onClick={() => {
                  sound.play('ui_tick');
                  change('quality', q.id);
                }}
              >
                {q.label}
              </button>
            ))}
          </div>
          <span class="hub-hint-line">
            The renderer picks its backend and shadow budget once at boot, so this takes effect the
            next time you reload.
          </span>
        </div>
        <div class="hub-field">
          <span class="hub-label">Passes</span>
          {RENDER_SWITCHES.map((sw) => (
            <label class="hub-check" key={sw.key}>
              <input
                type="checkbox"
                checked={s[sw.key]}
                onChange={(e) => {
                  sound.play('ui_tick');
                  change(sw.key, e.currentTarget.checked);
                }}
              />
              <span>
                {sw.label} <span class="hub-hint">{sw.hint}</span>
              </span>
            </label>
          ))}
          <span class="hub-hint-line">
            Each of these is also a URL flag (<code>?taa=0</code> and so on). Off is off from either
            side. Reload to apply.
          </span>
          <button
            type="button"
            class="hub-btn"
            onClick={() => {
              sound.play('ui_confirm');
              location.reload();
            }}
          >
            Apply and reload
          </button>
        </div>
      </Section>

      <Section title="Feedback" hint="">
        <label class="hub-check">
          <input
            type="checkbox"
            checked={s.screenshotDefault}
            onChange={(e) => {
              change('screenshotDefault', e.currentTarget.checked);
            }}
          />
          <span>
            Attach a screenshot by default
            <span class="hub-hint-line">
              The frame is captured from the game canvas when you open the Terminal. You can always
              turn it off per report.
            </span>
          </span>
        </label>
      </Section>

      <div class="hub-modal-acts">
        <button
          type="button"
          class="hub-btn hub-btn-quiet"
          onClick={() => {
            sound.play('ui_confirm');
            settings.reset();
            host.onSettingsChanged();
          }}
        >
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
